import React, { useState } from 'react';
import { statusStyle } from '../../utils/reportFormat';
import type { EpicStatusStep } from '../../utils/epicReport';
import * as fmt from '../../lib/format';

interface Props {
  /** Oldest first, like a story: the last line is where the epic stands at the end of the period. */
  steps: EpicStatusStep[];
  /** How many of the latest changes show before the earlier ones fold away. */
  visible?: number;
}

// An epic's status changes in the report's dot-and-pill style. A long history shows its latest changes
// and folds the earliest ones away above them, so one busy epic does not push the whole list down and
// the current status is always in view.
export default function StatusTimeline({ steps, visible = 4 }: Props) {
  const [expanded, setExpanded] = useState(false);
  if (steps.length === 0) return <span className="text-gray-300" aria-hidden>—</span>;

  // One hidden change is not worth a toggle: show it.
  const foldable = steps.length > visible + 1;
  const folded = foldable && !expanded;
  const shown = folded ? steps.slice(steps.length - visible) : steps;
  const hidden = steps.length - shown.length;

  return (
    <div>
      {foldable && (
        <button
          type="button"
          // The whole row opens the epic summary; this toggle must not.
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          aria-expanded={expanded}
          className="mb-2 rounded text-xs font-medium text-blue-600 transition-colors hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 motion-reduce:transition-none"
        >
          {folded ? `${hidden} earlier ${hidden === 1 ? 'change' : 'changes'}` : 'Show fewer'}
        </button>
      )}
      <ol className="relative space-y-3 text-left">
        {shown.map((s, i) => {
          const c = statusStyle(s.status);
          const last = i === shown.length - 1;
          return (
            <li key={`${s.created_at}-${i}`} className="relative flex items-start gap-3 pl-0.5">
              {!last && <span className="absolute left-[6px] top-3.5 h-[calc(100%+0.25rem)] w-px bg-gray-200" aria-hidden />}
              <span className={`relative z-10 mt-1 h-3 w-3 shrink-0 rounded-full ring-2 ring-white ${c.dot}`} aria-hidden />
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ring-1 ring-inset ${c.badge} ${
                    last ? 'font-semibold' : 'font-medium'
                  }`}
                >
                  {s.status}
                </span>
                <span className="whitespace-nowrap text-[0.6875rem] tabular-nums text-gray-500">{fmt.date(s.created_at)}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
